import {
  useContext, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import { round, isNil, kebabCase } from 'lodash';
import { usePermissions, UserContext } from '../../../../providers/userContextProvider';
import { getBackendLanguage, translateBackend } from '../../../../assets/i18n/config';
import {
  Branch,
  FinancialProduct,
  FinancialProductState,
  Strategy,
  Theme,
} from '../../../../interfaces';
import { ModelPortfolioData } from '../../../4-module/workflowCompletion/subSteps';
import {
  Box, Grid, Skeleton,
  Typography,
} from '../../../1-primative';
import {
  Card,
  CardContent,
  Button,
  MenuItem,
  SelectField,
  TextField,
  TranslatableTextField,
  Switch,
} from '../../../2-component';
import { FETCH_MODEL_PORTFOLIO, UPDATE_MODEL_PORTFOLIO } from '../../../4-module/workflowCompletion/subSteps/queries';
import { FETCH_THEMES } from '../../../../pages/buildModels';
import { SecuritySelect } from '../../../3-pattern/securitySelect/securitySelect';
import { Security } from '../../../3-pattern/securitiesSelectionTable/securitiesSelectionTable';
import { formatChildren } from '../../../4-module/workflowCompletion/subSteps/utils/useSaveModelPortfolio';
import { formatTranslatedField } from './modelPortfolioPolicyAndAssets';
import { useGlobalToast } from '../../../../providers/globalToastProvider';
import { NumberField } from '../../../3-pattern/numberField/numberField';
import { hasModelPortfolioChanges } from '../../../4-module/createModelPortfolioworkflow/createModelPortfolioWorkflow';

interface ThemeItem {
  themeId?: string;
  themeName?: string;
}

const options = {
  strategy: {
    required: 'REQUIRED',
  },
  translatedName: {
    required: 'REQUIRED',
  },
  translatedPortfolioDescription: {
    required: 'REQUIRED',
  },
  translatedInvestmentObjective: {
    required: 'REQUIRED',
  },
  riskProfileName: {
    required: 'NOT_REQUIRED',
  },
  theme: {
    required: 'REQUIRED',
  },
  suggestable: {
    required: 'NOT_REQUIRED',
  },
  zeroPercentCash: {
    required: 'NOT_REQUIRED',
  },
  lowThresholdScore: {
    required: 'REQUIRED',
  },
  suggestedMinScore: {
    required: 'REQUIRED',
  },
  suggestedMaxScore: {
    required: 'REQUIRED',
  },
  forecastedRateOfReturn: {
    required: 'NOT_REQUIRED',
  },
  forecastedStandardDeviation: {
    required: 'NOT_REQUIRED',
  },
  riskReturnLevel: {
    required: 'NOT_REQUIRED',
  },
  url: {
    required: 'NOT_REQUIRED',
  },
};

const sortThemeItems = (themeItems: ThemeItem[]) => {
  const currentLanguage = getBackendLanguage();
  const collator = new Intl.Collator(currentLanguage);
  const sorted = [...themeItems].sort((a, b) => collator.compare(a.themeName ?? '', b.themeName ?? ''));
  return sorted;
};

const ModelPortfolioGeneralDetails = ({
  model,
  setModel,
  loading,
  onSave,
}: {
  model: ModelPortfolioData,
  setModel: (v: ModelPortfolioData) => void,
  loading: boolean,
  onSave: (v: ModelPortfolioData) => void,
}) => {
  const { permissions } = usePermissions();
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation(['shared', 'modelPortfolios', 'buildModels']);
  const [themeOptions, setThemeOptions] = useState<ThemeItem[]>([]);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [cashEquivalentSecurity, setCashEquivalentSecurity] = useState<Security | null>(
    model?.children?.find((child: Branch) => child.isCashEquivalent) as Security | null,
  );
  const [initialPortfolio, setInitialPortfolio] = useState<ModelPortfolioData>({
    ...model,
    strategy: Strategy.STANDARD,
  });
  const { showToast } = useGlobalToast();
  const [portfolioToUpdate, setPortfolioToUpdate] = useState<ModelPortfolioData>(initialPortfolio);
  const hasChanges = useMemo(() => hasModelPortfolioChanges(portfolioToUpdate, initialPortfolio), [portfolioToUpdate, initialPortfolio]);

  useEffect(() => {
    setInitialPortfolio({
      ...model,
      strategy: Strategy.STANDARD,
    });
    setPortfolioToUpdate({
      ...model,
      strategy: Strategy.STANDARD,
    });
  }, [model]);

  useEffect(() => {
    const cashEquivalent = model?.children?.find((child: Branch) => child.isCashEquivalent) as Security | null;
    setCashEquivalentSecurity(cashEquivalent);
  }, [model?.children]);

  const { loading: themesLoading } = useQuery(FETCH_THEMES, {
    skip: !activeOrganization?.id,
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id,
        },
      },
    },
    onCompleted: (themeData) => {
      const newOptions = themeData.fetchThemes.themes.map((th: Theme) => ({
        themeId: th.id,
        themeName: translateBackend(th?.translatedName),
      }));
      setThemeOptions(sortThemeItems(newOptions));
    },
  });

  const canWrite = (): boolean => (permissions.includes('write:model_portfolios') || false);
  const canRead = (): boolean => (permissions.includes('read:model_portfolios') || false);

  const [updateModel, { loading: updating }] = useMutation(UPDATE_MODEL_PORTFOLIO, {
    variables: {
      input: {
        modelPortfolioId: portfolioToUpdate.id,
        translatedName: formatTranslatedField(portfolioToUpdate.translatedName || { en: '', fr: '' }),
        translatedPortfolioDescription: formatTranslatedField(portfolioToUpdate.translatedPortfolioDescription || { en: '', fr: '' }),
        translatedInvestmentObjective: formatTranslatedField(portfolioToUpdate.translatedInvestmentObjective || { en: '', fr: '' }),
        riskProfileName: formatTranslatedField(portfolioToUpdate.riskProfileName || { en: '', fr: '' }),
        themeId: portfolioToUpdate.theme?.id,
        suggestable: portfolioToUpdate.suggestable,
        zeroPercentCash: portfolioToUpdate.zeroPercentCash,
        children: formatChildren(portfolioToUpdate.children as Security[]),
        strategy: portfolioToUpdate.strategy,
        forecastedRateOfReturn: portfolioToUpdate.forecastedRateOfReturn
          ? round(portfolioToUpdate.forecastedRateOfReturn, 2)
          : undefined,
        forecastedStandardDeviation: portfolioToUpdate.forecastedStandardDeviation
          ? round(portfolioToUpdate.forecastedStandardDeviation, 2)
          : undefined,
        lowThresholdScore: !isNil(portfolioToUpdate.lowThresholdScore)
          ? round(portfolioToUpdate.lowThresholdScore, 2)
          : undefined,
        suggestedMinScore: !isNil(portfolioToUpdate.suggestedMinScore)
          ? round(portfolioToUpdate.suggestedMinScore, 2)
          : undefined,
        suggestedMaxScore: portfolioToUpdate.suggestedMaxScore
          ? round(portfolioToUpdate.suggestedMaxScore, 2)
          : undefined,
        riskReturnLevel: portfolioToUpdate.riskReturnLevel
          ? round(portfolioToUpdate.riskReturnLevel, 2)
          : undefined,
        url: portfolioToUpdate.url,
      },
    },
    refetchQueries: [FETCH_MODEL_PORTFOLIO],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      setInitialPortfolio(portfolioToUpdate);
      showToast({
        message: t('modelPortfolios:portfolio.success'),
        severity: 'success',
      });
    },
  });

  const validateField = (fieldName: string, value: any): boolean => {
    if (options[fieldName as keyof typeof options]?.required === 'NOT_REQUIRED') {
      return true;
    }
    switch (fieldName) {
      case 'translatedName':
      case 'translatedPortfolioDescription':
      case 'riskProfileName':
        return value?.[getBackendLanguage()]?.trim()?.length > 0;

      case 'lowThresholdScore':
      case 'suggestedMinScore':
        return value !== undefined && value !== null && value !== '';

      case 'suggestedMaxScore': {
        const minScore = portfolioToUpdate.suggestedMinScore;
        return value !== undefined && value !== null && value !== ''
          && (minScore === undefined || minScore === null || parseFloat(value) > minScore);
      }

      default:
        return value !== undefined && value !== null && value !== '';
    }
  };

  const isCashEquivalentDisabledWith = useMemo(() => {
    if (!portfolioToUpdate.zeroPercentCash) {
      return '';
    }

    if (model?.state === FinancialProductState.ACTIVE) {
      const ticker = cashEquivalentSecurity?.financialProduct?.ticker;
      const name = translateBackend(cashEquivalentSecurity?.financialProduct?.translatedName);
      return ticker && name ? `${ticker} - ${name}` : name;
    }

    return null;
  }, [portfolioToUpdate.zeroPercentCash, model?.state, cashEquivalentSecurity?.financialProduct?.translatedName, cashEquivalentSecurity?.financialProduct?.ticker]);

  const handleFieldBlur = (fieldName: string) => {
    if (!portfolioToUpdate) return;
    const isValid = validateField(fieldName, portfolioToUpdate[fieldName as keyof typeof portfolioToUpdate]);
    setValidationErrors((prev: Set<string>) => {
      const newErrors = new Set(prev);
      if (!isValid) {
        newErrors.add(fieldName);
      } else {
        newErrors.delete(fieldName);
      }
      return newErrors;
    });
  };

  const isFormValid = () => {
    const requiredFields = Object.keys(options).filter(
      (key) => {
        if (options[key as keyof typeof options]?.required === 'NOT_REQUIRED') return false;
        return true;
      },
    );
    return requiredFields.every((field) => validateField(field, portfolioToUpdate[field as keyof typeof portfolioToUpdate]));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    try {
      await updateModel();
      onSave(portfolioToUpdate);
      showToast({
        message: t('modelPortfolios:portfolio.success'),
        severity: 'success',
      });
    } catch (error) {
      showToast({
        message: t('modelPortfolios:portfolio.error'),
        severity: 'error',
      });
    }
  };

  const handleCancel = () => {
    setPortfolioToUpdate(initialPortfolio);
    setModel(initialPortfolio);
  };

  const handleCashEquivalentSecurityChange = (security: FinancialProduct) => {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    const child: Security = {
      financialProduct: security,
      isCashEquivalent: true,
      percentage: 0,
      taxRank: 1,
      id: tempId,
      color: 'supportOne',
    };

    const updatedChildren = [...(portfolioToUpdate.children || []).filter(
      (updateChild: Branch) => !updateChild.isCashEquivalent,
    ), child];

    setPortfolioToUpdate({
      ...portfolioToUpdate,
      children: updatedChildren,
    });

    setCashEquivalentSecurity(child);
  };

  if (loading || themesLoading) {
    return (
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={12}>
              <Skeleton width="100%" height="112px" />
            </Grid>
            <Grid item xs={12}>
              <Skeleton width="100%" height="112px" />
            </Grid>
            <Grid item xs={12}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton width="100%" height="36px" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton width="100%" height="36px" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={6}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={12}>
              <Skeleton width="100%" height="56px" />
            </Grid>
            <Grid item xs={10}>
              <Skeleton width="200px" height="24px" />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Skeleton width="80px" height="36px" sx={{ mr: 2 }} />
                <Skeleton width="80px" height="36px" />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  if (!canRead()) {
    return (
      <Box>
        <Typography
          variant="displayLarge"
          sx={{ fontWeight: 600, fontSize: '20px' }}
        >
          {t('modelPortfolios:portfolio.noAccess')}
        </Typography>
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Grid container spacing={2} component='form' justifyContent='flex-end'>
          <Grid item xs={6}>
            <SelectField
              testId="model-type"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value as Strategy;
                if (Object.values(Strategy).includes(value)) {
                  setPortfolioToUpdate({
                    ...portfolioToUpdate,
                    strategy: value,
                  });
                }
              }}
              disabled={loading}
              label={t('modelPortfolios:createModelPortfolio.type')}
              fullWidth
              value={portfolioToUpdate?.strategy ?? Strategy.STANDARD}
              onBlur={() => handleFieldBlur('strategy')}
              error={validationErrors.has('strategy')}
              required={options.strategy.required === 'REQUIRED'}
            >
              {Object.values(Strategy).map((strategy) => (
                <MenuItem
                  key={strategy}
                  value={strategy}
                  data-testid={`type-${kebabCase(strategy)}`}
                >
                  {t(`modelPortfolios:modelType.${strategy}`)}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
          <Grid item xs={6}>
            <TranslatableTextField
              testId="model-name"
              onChange={(e) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                translatedName: e,
              })}
              value={portfolioToUpdate?.translatedName ?? {}}
              fullWidth
              required={options.translatedName.required === 'REQUIRED'}
              onBlur={() => handleFieldBlur('translatedName')}
              error={validationErrors.has('translatedName')}
              label={t('modelPortfolios:modelPortfolioTable.name')}
            />
          </Grid>
          <Grid item xs={6}>
            <TranslatableTextField
              testId="model-description"
              onChange={(e) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                translatedPortfolioDescription: e,
              })}
              value={portfolioToUpdate?.translatedPortfolioDescription ?? {}}
              fullWidth
              multiline={true}
              rows={3}
              required={options.translatedPortfolioDescription.required === 'REQUIRED'}
              onBlur={() => handleFieldBlur('translatedPortfolioDescription')}
              error={validationErrors.has('translatedPortfolioDescription')}
              label={t('modelPortfolios:modelPortfolioTable.description')}
            />
          </Grid>
          <Grid item xs={6}>
            <TranslatableTextField
              testId="model-investment-objective"
              onChange={(e) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                translatedInvestmentObjective: e,
              })}
              value={portfolioToUpdate?.translatedInvestmentObjective ?? {}}
              fullWidth
              multiline={true}
              rows={3}
              required={options.translatedInvestmentObjective.required === 'REQUIRED'}
              onBlur={() => handleFieldBlur('translatedInvestmentObjective')}
              error={validationErrors.has('translatedInvestmentObjective')}
              label={t('buildModels:edit.investmentObjective')}
            />
          </Grid>
          <Grid item xs={4}>
            <TranslatableTextField
              testId="risk-profile"
              onChange={(e) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                riskProfileName: e,
              })}
              value={portfolioToUpdate?.riskProfileName ?? {}}
              fullWidth
              required={options.riskProfileName.required === 'REQUIRED'}
              onBlur={() => handleFieldBlur('riskProfileName')}
              error={validationErrors.has('riskProfileName')}
              label={t('modelPortfolios:portfolioDetails.details.riskProfile')}
            />
          </Grid>
          <Grid item xs={4}>
            {themeOptions && (<SelectField
              testId="model-family"
              value={portfolioToUpdate?.theme?.id || ''}
              fullWidth
              required={options.theme.required === 'REQUIRED'}
              onBlur={() => handleFieldBlur('theme')}
              error={validationErrors.has('theme')}
              label={t('modelPortfolios:modelPortfolioTable.modelFamily')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newThemeId = e.target.value;
                const selectedTheme = themeOptions.find((th) => th.themeId === newThemeId);
                if (selectedTheme) {
                  setPortfolioToUpdate({
                    ...portfolioToUpdate,
                    theme: {
                      id: selectedTheme.themeId,
                      translatedName: { en: selectedTheme.themeName || '' },
                    } as Theme,
                  });
                }
              }}
            >
              {themeOptions.map((th: ThemeItem, index) => (
                <MenuItem key={index} value={th.themeId}
                  data-testid={`type-${kebabCase(th.themeName)}`}>
                  {th.themeName}
                </MenuItem>
              ))}
            </SelectField>)}
          </Grid>
          <Grid item xs={4}>
            <NumberField
              testId="risk-return-level"
              setNumber={(number) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                riskReturnLevel: parseFloat(number),
              })}
              number={portfolioToUpdate.riskReturnLevel?.toString() ?? ''}
              label={t('buildModels:edit.riskReturnLevel')}
              InputProps={{
                readOnly: !canWrite(),
              }}
              unsigned
            />
          </Grid>
          <Grid item xs={2}>
            <Switch
              testId="suggestable"
              sx={{ pt: '20px' }}
              onChange={(e: boolean) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                suggestable: e,
              })}
              disabled={loading}
              label={t('buildModels:edit.suggestable')}
              checked={portfolioToUpdate?.suggestable !== undefined ? portfolioToUpdate.suggestable : false}
            />
          </Grid>
          <Grid item xs={2}>
            <Switch
              testId="zero-percent-cash"
              sx={{ pt: '20px' }}
              onChange={(e: boolean) => {
                if (!e) {
                  const updatedChildren = (portfolioToUpdate.children || [])?.filter((child) => !child.isCashEquivalent);
                  setPortfolioToUpdate({
                    ...portfolioToUpdate,
                    zeroPercentCash: e,
                    children: updatedChildren,
                  });
                  setPortfolioToUpdate({
                    ...portfolioToUpdate,
                    zeroPercentCash: e,
                    children: updatedChildren,
                  });
                  setCashEquivalentSecurity(null);
                }
                setPortfolioToUpdate({
                  ...portfolioToUpdate,
                  zeroPercentCash: e,
                });
              }}
              disabled={model?.state === FinancialProductState.ACTIVE}
              label={t('modelPortfolios:portfolioDetails.details.zeroPercentCash')}
              checked={portfolioToUpdate?.zeroPercentCash !== undefined ? portfolioToUpdate.zeroPercentCash : false}
            />
          </Grid>
          <Grid item xs={8}>
            <SecuritySelect
              value={cashEquivalentSecurity?.financialProduct?.id || ''}
              label={t('modelPortfolios:cashEquivalentSecurity')}
              setSecurity={(security) => handleCashEquivalentSecurityChange(security)}
              disabledWith={isCashEquivalentDisabledWith}
              inputError={portfolioToUpdate.zeroPercentCash && !cashEquivalentSecurity}
            />
          </Grid>
          <Grid item xs={2.4}>
            <NumberField
              testId="low-threshold-score"
              setNumber={(number) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                lowThresholdScore: parseFloat(number),
              })}
              number={portfolioToUpdate.lowThresholdScore?.toString() ?? ''}
              label={t('buildModels:edit.lowThresholdScore')}
              required={options.lowThresholdScore.required === 'REQUIRED'}
              onBlur={() => handleFieldBlur('lowThresholdScore')}
              error={validationErrors.has('lowThresholdScore')}
              InputProps={{
                readOnly: !canWrite(),
              }}
              decimalPlaces={2}
              unsigned
            />
          </Grid>
          <Grid item xs={2.4}>
            <NumberField
              testId="suggested-min-score"
              setNumber={(number) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                suggestedMinScore: parseFloat(number),
              })}
              number={portfolioToUpdate.suggestedMinScore?.toString() ?? ''}
              label={t('buildModels:edit.suggestedMinScore')}
              required={options.suggestedMinScore.required === 'REQUIRED'}
              onBlur={() => handleFieldBlur('suggestedMinScore')}
              error={validationErrors.has('suggestedMinScore')}
              InputProps={{
                readOnly: !canWrite(),
              }}
              decimalPlaces={2}
              unsigned
            />
          </Grid>
          <Grid item xs={2.4}>
            <NumberField
              testId="suggested-max-score"
              setNumber={(number) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                suggestedMaxScore: parseFloat(number),
              })}
              number={portfolioToUpdate.suggestedMaxScore?.toString() ?? ''}
              label={t('buildModels:edit.suggestedMaxScore')}
              required={options.suggestedMaxScore.required === 'REQUIRED'}
              onBlur={() => handleFieldBlur('suggestedMaxScore')}
              error={validationErrors.has('suggestedMaxScore')}
              InputProps={{
                readOnly: !canWrite(),
              }}
              decimalPlaces={2}
              unsigned
            />
          </Grid>
          <Grid item xs={2.4}>
            <NumberField
              testId="forecasted-rate-of-return"
              setNumber={(number) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                forecastedRateOfReturn: parseFloat(number),
              })}
              number={portfolioToUpdate.forecastedRateOfReturn?.toString() ?? ''}
              label={t('buildModels:edit.forecastedRateOfReturn')}
              required={options.forecastedRateOfReturn.required === 'REQUIRED'}
              InputProps={{
                readOnly: !canWrite(),
              }}
              trailingIcon='percent'
              decimalPlaces={2}
              unsigned
            />
          </Grid>
          <Grid item xs={2.4}>
            <NumberField
              testId="forecasted-standard-deviation"
              setNumber={(number) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                forecastedStandardDeviation: parseFloat(number),
              })}
              number={portfolioToUpdate.forecastedStandardDeviation?.toString() ?? ''}
              label={t('buildModels:edit.forecastedStandardDeviation')}
              required={options.forecastedStandardDeviation.required === 'REQUIRED'}
              InputProps={{
                readOnly: !canWrite(),
              }}
              trailingIcon='percent'
              decimalPlaces={2}
              unsigned
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              testId="url"
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPortfolioToUpdate({
                ...portfolioToUpdate,
                url: e.target.value,
              })}
              value={portfolioToUpdate?.url}
              label={t('modelPortfolios:portfolioDetails.details.fundamentalDetailsLink')}
              required={options.url.required === 'REQUIRED'}
            />
          </Grid>
          <Grid item xs={12}>
            {canWrite() && (
              <Grid item>
                <Box sx={{ display: 'flex', alignItems: 'right', justifyContent: 'right' }}>
                  <Button
                    variant='tonal'
                    disabled={!hasChanges}
                    label={t('cancel')}
                    onClick={handleCancel}
                    size={'sm'}
                    sx={{ mr: 2 }}
                  />
                  <Button
                    variant='filled'
                    disabled={!hasChanges || !isFormValid()}
                    label={t('save')}
                    onClick={handleSubmit}
                    size={'sm'}
                    loading={updating}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ModelPortfolioGeneralDetails;
